import Component from '@glimmer/component';
import type Link from 'ember-link/link';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { gt } from 'ember-truth-helpers';
import TabItem from 'garaje/components/ui/classic/tab-item';
import isRouteActive from 'garaje/helpers/is-route-active';

interface Tab {
  label: string;
  link: Link;
  routeNames?: string | string[];
  routeParams?: Record<string, unknown>;
  isNew?: boolean;
  icon?: { name: string; path: string; currentPath: string };
  tooltip?: string;
  class?: string;
}

interface TabNavSignature {
  Args: {
    /**
     * a collection of TabItem objects to render
     */
    tabs: Tab[];
    /**
     * determines whether TabItems are sorted by their label
     */
    isSorted?: boolean;
  };
  Element: HTMLElement;
}

/**
 * Renders a nav element containing TabItems if TabItems.length > 1
 */
export default class TabNav extends Component<TabNavSignature> {
  get tabs(): Tab[] {
    if (this.args.isSorted) return this.args.tabs.toSorted((a, b) => a.label.localeCompare(b.label));
    return this.args.tabs;
  }

  <template>
    {{#if (gt @tabs.length 1)}}
      <nav class="tabs" data-test-tab-nav ...attributes>
        {{#each this.tabs key="label" as |tab|}}
          <TabItem
            @link={{tab.link}}
            @isActive={{isRouteActive tab.routeNames tab.routeParams}}
            @isNew={{tab.isNew}}
            data-test-tab-item={{tab.label}}
            class={{tab.class}}
          >
            {{#if tab.icon}}
              {{#if (isRouteActive tab.routeNames tab.routeParams)}}
                <span class="mr-1 h-6 w-6 bg-red-40 rounded-full inline-flex items-center justify-center">
                  <img
                    src={{tab.icon.currentPath}}
                    data-test-icon={{tab.icon.name}}
                    alt={{tab.icon.name}}
                    class="h-3 w-3"
                  />
                </span>
              {{else}}
                <span class="mr-1 h-6 w-6 bg-carbon-10 rounded-full inline-flex items-center justify-center">
                  <img src={{tab.icon.path}} data-test-icon={{tab.icon.name}} alt={{tab.icon.name}} class="h-3 w-3" />
                </span>
              {{/if}}
            {{/if}}
            {{tab.label}}
            {{#if tab.tooltip}}
              <EmberTooltip @side="top">
                <h4>{{tab.tooltip}}</h4>
              </EmberTooltip>
            {{/if}}
          </TabItem>

        {{/each}}
      </nav>
    {{/if}}
  </template>
}
